/**
 * Reducer for the lexicons module (VE-24948 / U5).
 *
 * Duck convention, `createReducer` from Redux Toolkit — not `createSlice`, per
 * the repo's module pattern. All state is treated as immutable; every handler
 * returns a new object rather than mutating a draft, matching the surrounding
 * modules.
 */

import { createReducer } from '@reduxjs/toolkit';
import { keyBy } from 'lodash';

import {
  ADD_DRAFT_ENTRIES,
  ADD_LEXICON_ENTRIES,
  CANCEL_CSV_IMPORT,
  CLEAR_DELETED_LEXICONS,
  CLEAR_LEXICON_CONFLICT,
  CLEAR_MUTATION_NOTICE,
  LEXICON_MUTATION_SETTLED,
  CLOSE_LEXICON_DRAWER,
  CREATE_LEXICON,
  DELETE_LEXICON,
  FETCH_LEXICONS,
  FETCH_LEXICONS_FAILURE,
  FETCH_LEXICONS_SUCCESS,
  RELOAD_EDITING_LEXICON_SUCCESS,
  LEXICON_DELETED,
  LEXICON_MUTATION_AUDIT_INCOMPLETE,
  LEXICON_MUTATION_BLOCKED_BY_AUDIT,
  LEXICON_MUTATION_FAILURE,
  LEXICON_MUTATION_SUCCESS,
  LEXICON_SOFT_DELETE_CHANGED,
  LEXICONS_NOT_CONFIGURED,
  OPEN_LEXICON_DRAWER,
  PURGE_DELETED_DRAFT_ENTRIES,
  PURGE_DRAFT_ENTRY,
  REMOVE_DRAFT_ENTRY,
  RESTORE_DRAFT_ENTRY,
  UPDATE_DRAFT_ENTRY,
  REMOVE_LEXICON_ENTRIES,
  RENAME_LEXICON,
  SAVE_LEXICON_DRAFT,
  SET_CSV_IMPORT_RESULT,
  SET_DRAFT_NAME,
  SET_DRAFT_NAME_ERROR,
  SET_LEXICON_CONFLICT,
  SET_LEXICON_FLAGS,
  FETCH_DELETED_LEXICONS,
  FETCH_DELETED_LEXICONS_SUCCESS,
  FETCH_DELETED_LEXICONS_FAILURE,
  START_CSV_IMPORT,
} from '../actions';
import {
  DRAFT_ENTRY_ID_PREFIX,
  Lexicon,
  LexiconEntry,
  LexiconsState,
  deletedEntries,
  isDraftEntryId,
  liveEntries,
} from '../models';
import { defaultState, namespace } from '../store';
import { lexiconEntryDuplicateKey } from '../csvValidator';
import { LEXICON_MAX_ENTRIES } from '../limits';

const emptyDraft = {
  name: '',
  entries: [] as readonly Lexicon['entries'][0][],
};

/**
 * Drops `arriving` from the slot on the OTHER side of the soft-delete line.
 *
 * THE INVARIANT IS "AN ID IS IN AT MOST ONE SLOT", and it has to be total.
 * `intoItsSlot` below enforces it for the eager writes, but a row that is in
 * both lists is destructive — the trash view renders Permanently delete, and
 * on a lexicon that is actually live that is an irreversible loss (VE-30783
 * review B1) — so it cannot depend on the eager write having run. Two cases
 * where it does not:
 *
 *   - the write landed and its audit OUTCOME failed, so the saga threw
 *     `LexiconAuditIncompleteError` before it could move the row, and only
 *     the catch-path refetches are left;
 *   - the trailing refetch read an index that had not caught up to the write,
 *     or another admin changed the row meanwhile.
 *
 * So each list response is treated as AUTHORITATIVE for the ids it carries:
 * the server answered the `isDeleted` predicate for each one, and whichever
 * response lands last wins. The rows can be stale — they cannot be in both
 * places at once, which is the part that destroys data.
 *
 * Returns the slot unchanged when nothing moved, so the list selectors above
 * it keep their memoised sort.
 */
function evictedFrom(
  slot: LexiconsState['lexicons'],
  arriving: readonly LexiconsState['lexicons'][string][]
): LexiconsState['lexicons'] {
  if (!arriving.some(({ id }) => id in slot)) {
    return slot;
  }
  const next = { ...slot };
  arriving.forEach(({ id }) => delete next[id]);
  return next;
}

/**
 * Puts a written summary in the slot its own `isDeleted` flag implies, and
 * takes it out of the other one.
 *
 * This is the FAST path to the invariant `evictedFrom` also enforces: it moves
 * the row the instant the registry confirms the write, rather than leaving it
 * to whichever refetch lands next. Removing it from the far slot matters as
 * much as adding it to the near one — a restored lexicon left in
 * `deletedLexicons` is still rendered in the trash, still counted by the "(N)"
 * on the link, and still offers Permanently delete.
 *
 * Shared by the two actions that can change a row's side: the soft
 * delete/restore pair (`LEXICON_SOFT_DELETE_CHANGED`) and any ordinary write
 * that comes back with the flag already flipped — a concurrent soft delete by
 * another admin surfacing through a rename or a draft save.
 */
function intoItsSlot(
  state: LexiconsState,
  summary: LexiconsState['lexicons'][string]
): Pick<LexiconsState, 'lexicons' | 'deletedLexicons'> {
  const { [summary.id]: _wasLive, ...liveRest } = state.lexicons;
  const { [summary.id]: _wasDeleted, ...deletedRest } = state.deletedLexicons;
  return summary.isDeleted
    ? {
        lexicons: liveRest,
        deletedLexicons: { ...deletedRest, [summary.id]: summary },
      }
    : {
        lexicons: { ...liveRest, [summary.id]: summary },
        deletedLexicons: deletedRest,
      };
}

export const lexiconsReducer = {
  [namespace]: createReducer(defaultState, (builder) => {
    builder
      .addCase(FETCH_LEXICONS, (state) => ({ ...state, isFetching: true }))
      .addCase(FETCH_LEXICONS_SUCCESS, (state, { payload }) => ({
        ...state,
        isFetching: false,
        isNotConfigured: false,
        // The WHOLE live library, replacing it.
        lexicons: keyBy(payload.lexicons, 'id'),
        // ...and out of the trash, because this response is AUTHORITATIVE for
        // the ids it carries: the server answered `isDeleted: 'false'` for
        // each one. Without this the slots are only disjoint when the eager
        // move runs, which is not always (see `evictedFrom`).
        deletedLexicons: evictedFrom(state.deletedLexicons, payload.lexicons),
      }))
      .addCase(FETCH_DELETED_LEXICONS, (state) => ({
        ...state,
        isFetchingDeleted: true,
      }))
      .addCase(FETCH_DELETED_LEXICONS_SUCCESS, (state, { payload }) => {
        // IGNORED WHEN NOTHING IS WAITING FOR IT. `CLEAR_DELETED_LEXICONS`
        // lowers the flag on unmount, and this fetch is not cancelled by
        // that — a mutation's trailing refetch is dispatched after the
        // cleanup, not racing it — so without this check a response landing
        // after the user left would seat the whole trash, names and emails,
        // back in the store for the session.
        if (!state.isFetchingDeleted) {
          return state;
        }
        return {
          ...state,
          isFetchingDeleted: false,
          isNotConfigured: false,
          deletedLexicons: keyBy(payload.lexicons, 'id'),
          lexicons: evictedFrom(state.lexicons, payload.lexicons),
        };
      })
      .addCase(FETCH_DELETED_LEXICONS_FAILURE, (state) => ({
        ...state,
        isFetchingDeleted: false,
      }))
      .addCase(CLEAR_DELETED_LEXICONS, (state) => ({
        ...state,
        deletedLexicons: {},
        // Lowered so a response still in flight is dropped rather than
        // refilling the slot we just emptied.
        isFetchingDeleted: false,
      }))
      /**
       * Taking the other actor's version, after a BR-6 conflict.
       *
       * Persisted entries are replaced by the fresh ones and the not-yet-saved
       * (`draft:`) entries are KEPT — the user's unsaved work is not what the
       * conflict was about. The baseline moves to the fresh revision so the
       * next save is guarded against what they just accepted, and the banner
       * clears.
       *
       * This used to hang off `FETCH_LEXICONS_SUCCESS`, back when the list
       * carried entries. It carries summaries now, so the reload is its own
       * read and its own action.
       */
      .addCase(RELOAD_EDITING_LEXICON_SUCCESS, (state, { payload }) => {
        const fresh = payload.lexicon;
        if (!state.editing || state.editing.lexiconId !== fresh.id) {
          return state;
        }
        const { conflict: _cleared, ...rest } = state.editing;
        return {
          ...state,
          isFetching: false,
          editing: {
            ...rest,
            draft: {
              ...rest.draft,
              entries: [
                ...fresh.entries,
                ...rest.draft.entries.filter((e) => isDraftEntryId(e.entryId)),
              ],
            },
            baseRevision: fresh.revision,
            baseline: { name: fresh.name, entries: fresh.entries },
          },
        };
      })
      .addCase(FETCH_LEXICONS_FAILURE, (state) => ({
        ...state,
        isFetching: false,
      }))
      .addCase(LEXICONS_NOT_CONFIGURED, (state) => ({
        ...state,
        isFetching: false,
        isFetchingDeleted: false,
        isNotConfigured: true,
        // Deliberately clears BOTH lists: showing stale lexicons while the
        // feature reports itself not configured would be worse than showing none.
        lexicons: {},
        deletedLexicons: {},
      }))
      /**
       * Optimistic, and the saga is what makes it safe: a failed write
       * re-fetches the page, so the tick flicks back rather than lying.
       *
       * Ignores an id the page does not hold — a toggle can only come from a
       * rendered row, but a reducer that invents a row from a stale action
       * would put a lexicon with no name in the table.
       */
      .addCase(SET_LEXICON_FLAGS, (state, { payload }) => {
        const current = state.lexicons[payload.lexiconId];
        if (!current) {
          return state;
        }
        return {
          ...state,
          lexicons: {
            ...state.lexicons,
            [payload.lexiconId]: { ...current, ...payload.flags },
          },
        };
      })
      .addCase(OPEN_LEXICON_DRAWER, (state, { payload }) => {
        const existing = payload.lexiconId
          ? state.lexicons[payload.lexiconId]
          : undefined;
        return {
          ...state,
          isDrawerOpen: true,
          // OPENS WITHOUT ENTRIES, and a saga fills them in. The list holds
          // summaries — that is the point of the split — so the entries have to
          // be read, and `onOpenLexiconDrawer` reads them and dispatches
          // RELOAD_EDITING_LEXICON_SUCCESS, which is the same case that serves
          // the conflict banner's Reload. `isFetching` keeps the empty state
          // from flashing in the gap.
          isFetching: !!payload.lexiconId,
          editing: {
            ...(payload.lexiconId ? { lexiconId: payload.lexiconId } : {}),
            draft: existing ? { name: existing.name, entries: [] } : emptyDraft,
            // Captured on open — this is the value BR-6 compares against when
            // the write happens. Re-reading it later would defeat the guard.
            ...(existing
              ? {
                  baseRevision: existing.revision,
                  baseline: { name: existing.name, entries: [] },
                }
              : {}),
            entryErrors: {},
          },
        };
      })
      .addCase(CLOSE_LEXICON_DRAWER, (state) => {
        const { editing: _editing, csvImport: _csvImport, ...rest } = state;
        return { ...rest, isDrawerOpen: false };
      })
      .addCase(SET_DRAFT_NAME, (state, { payload }) =>
        state.editing
          ? {
              ...state,
              editing: {
                ...state.editing,
                draft: { ...state.editing.draft, name: payload.name },
              },
            }
          : state
      )
      .addCase(SET_DRAFT_NAME_ERROR, (state, { payload }) => {
        if (!state.editing) {
          return state;
        }
        const { nameError: _dropped, ...editing } = state.editing;
        return {
          ...state,
          editing: {
            ...editing,
            ...(payload.message ? { nameError: payload.message } : {}),
          },
        };
      })
      .addCase(ADD_DRAFT_ENTRIES, (state, { payload }) => {
        if (!state.editing) {
          return state;
        }
        const current = state.editing.draft.entries;
        const seen = new Set(current.map(lexiconEntryDuplicateKey));
        // Next free draft index: highest `draft:N` currently in the draft + 1.
        // Uniqueness among CURRENT entries is all that is needed — a removed
        // draft entry is referenced by nothing once it is gone.
        let next =
          current
            .filter((e) => isDraftEntryId(e.entryId))
            .reduce(
              (max, e) =>
                Math.max(
                  max,
                  Number(e.entryId.slice(DRAFT_ENTRY_ID_PREFIX.length)) || 0
                ),
              -1
            ) + 1;
        const headroom = Math.max(0, LEXICON_MAX_ENTRIES - current.length);
        const added: LexiconEntry[] = [];
        for (const input of payload.entries) {
          if (added.length >= headroom) {
            break; // BR-16 — the UI gates this too; the cap here is the floor.
          }
          const key = lexiconEntryDuplicateKey(input);
          if (seen.has(key)) {
            continue; // BR-23 — duplicates within a lexicon are skipped.
          }
          seen.add(key);
          added.push({ entryId: `${DRAFT_ENTRY_ID_PREFIX}${next}`, ...input });
          next += 1;
        }
        if (added.length === 0) {
          return state;
        }
        return {
          ...state,
          editing: {
            ...state.editing,
            draft: {
              ...state.editing.draft,
              entries: [...current, ...added],
            },
          },
        };
      })
      .addCase(RESTORE_DRAFT_ENTRY, (state, { payload }) =>
        state.editing
          ? {
              ...state,
              editing: {
                ...state.editing,
                draft: {
                  ...state.editing.draft,
                  entries: state.editing.draft.entries.map((e) =>
                    e.entryId === payload.entryId
                      ? { ...e, isDeleted: false }
                      : e
                  ),
                },
              },
            }
          : state
      )
      .addCase(PURGE_DRAFT_ENTRY, (state, { payload }) =>
        state.editing
          ? {
              ...state,
              editing: {
                ...state.editing,
                draft: {
                  ...state.editing.draft,
                  // Only a tombstone can be purged — a live entry is deleted
                  // first, so one control can never skip the recoverable step.
                  entries: state.editing.draft.entries.filter(
                    (e) => !(e.entryId === payload.entryId && e.isDeleted)
                  ),
                },
              },
            }
          : state
      )
      .addCase(PURGE_DELETED_DRAFT_ENTRIES, (state) =>
        state.editing
          ? {
              ...state,
              editing: {
                ...state.editing,
                draft: {
                  ...state.editing.draft,
                  entries: state.editing.draft.entries.filter(
                    (e) => !e.isDeleted
                  ),
                },
              },
            }
          : state
      )
      .addCase(UPDATE_DRAFT_ENTRY, (state, { payload }) => {
        if (!state.editing) {
          return state;
        }
        const current = state.editing.draft.entries;
        const index = current.findIndex((e) => e.entryId === payload.entryId);
        if (index === -1) {
          return state;
        }
        // BR-23 against the OTHER entries only. Comparing against all of them
        // would make every edit that leaves the duplicate key alone — changing
        // a redaction code, relabelling Keyword to Phrase — collide with the
        // entry being edited, i.e. with itself.
        const key = lexiconEntryDuplicateKey(payload.entry);
        const collides = current.some(
          (e, i) => i !== index && lexiconEntryDuplicateKey(e) === key
        );
        if (collides) {
          return state;
        }
        const existing = current[index];
        if (!existing) {
          return state;
        }
        const next = [...current];
        // Id and position preserved — that is what makes this an edit rather
        // than a remove plus an append, which would move the row to the bottom.
        next[index] = { entryId: existing.entryId, ...payload.entry };
        return {
          ...state,
          editing: {
            ...state.editing,
            draft: { ...state.editing.draft, entries: next },
          },
        };
      })
      .addCase(REMOVE_DRAFT_ENTRY, (state, { payload }) =>
        state.editing
          ? {
              ...state,
              editing: {
                ...state.editing,
                draft: {
                  ...state.editing.draft,
                  // Tombstone what was saved before; splice what never was.
                  // See the action for why the two differ.
                  entries: isDraftEntryId(payload.entryId)
                    ? state.editing.draft.entries.filter(
                        (e) => e.entryId !== payload.entryId
                      )
                    : state.editing.draft.entries.map((e) =>
                        e.entryId === payload.entryId
                          ? { ...e, isDeleted: true }
                          : e
                      ),
                },
              },
            }
          : state
      )
      .addCase(SET_LEXICON_CONFLICT, (state, { payload }) =>
        state.editing
          ? {
              ...state,
              editing: {
                ...state.editing,
                conflict: {
                  modifiedBy: payload.modifiedBy,
                  modifiedDateTime: payload.modifiedDateTime,
                },
              },
            }
          : state
      )
      .addCase(CLEAR_LEXICON_CONFLICT, (state) => {
        if (!state.editing) {
          return state;
        }
        const { conflict: _dropped, ...editing } = state.editing;
        return { ...state, editing };
      })
      /**
       * The soft delete and the restore, moving the row between the lists.
       *
       * Eagerly, and that is the correctness point rather than a nicety: the
       * refetches these two sagas dispatch are reconciliation, and leaving the
       * move to them made a row's list depend on two independent requests both
       * landing. A failed deleted-fetch after a restore left the row rendered
       * in BOTH tables, where Permanently delete — the one irreversible
       * operation here — would destroy a lexicon that was no longer deleted.
       */
      .addCase(LEXICON_SOFT_DELETE_CHANGED, (state, { payload }) => ({
        ...state,
        ...intoItsSlot(state, payload.lexicon),
      }))
      /**
       * The PURGE, and only the purge.
       *
       * It drops the row from the TRASH, which is the only list a purgeable
       * lexicon can be in: a soft delete leaves the record in place with
       * `isDeleted` raised, which is what moved it out of the library and into
       * `deletedLexicons` in the first place. A permanent delete is the one
       * operation that really does destroy the row.
       */
      .addCase(LEXICON_DELETED, (state, { payload }) => {
        const { [payload.lexiconId]: _removed, ...deletedLexicons } =
          state.deletedLexicons;
        return { ...state, deletedLexicons };
      })
      .addCase(START_CSV_IMPORT, (state, { payload }) => ({
        ...state,
        csvImport: {
          fileName: payload.fileName,
          rowCount: payload.rowCount,
        },
      }))
      .addCase(SET_CSV_IMPORT_RESULT, (state, { payload }) =>
        state.csvImport
          ? {
              ...state,
              csvImport: { ...state.csvImport, result: payload.result },
            }
          : state
      )
      .addCase(CANCEL_CSV_IMPORT, (state) => {
        const { csvImport: _dropped, ...rest } = state;
        return rest;
      })

      // --- Mutation lifecycle (NFR-S4's UX half) --------------------------
      //
      // Five mutations share one in-flight flag and one outcome notice: the
      // page shows a single "did my change happen?" answer at a time, and the
      // three failure kinds are deliberately distinct — see
      // `LexiconMutationNotice` in models.ts.
      .addMatcher(
        (action) =>
          SAVE_LEXICON_DRAFT.match(action) ||
          CREATE_LEXICON.match(action) ||
          RENAME_LEXICON.match(action) ||
          ADD_LEXICON_ENTRIES.match(action) ||
          REMOVE_LEXICON_ENTRIES.match(action) ||
          DELETE_LEXICON.match(action),
        (state) => {
          const { mutationNotice: _dropped, ...rest } = state;
          return { ...rest, isMutating: true };
        }
      )
      .addMatcher(LEXICON_MUTATION_SUCCESS.match, (state, action) => {
        const written = action.payload.lexicon;
        // The dialog commits its draft in one Save and closes on success (the
        // saga dispatches CLOSE_LEXICON_DRAWER), so nothing here touches the
        // draft. The refetches the saga dispatches after every mutation are
        // what reconcile these lists; the written lexicon is stored eagerly so
        // the table is right for the round trip in between.
        const { mutationNotice: _dropped, ...rest } = state;
        if (!written) {
          return { ...rest, isMutating: false };
        }
        // Stored as a SUMMARY — the lists are summaries, and the entries the
        // write returned belong to the draft, not to this table.
        const summary = {
          id: written.id,
          name: written.name,
          revision: written.revision,
          entriesId: written.entriesId,
          entryCount: liveEntries(written.entries).length,
          deletedEntryCount: deletedEntries(written.entries).length,
          createdDateTime: written.createdDateTime,
          modifiedDateTime: written.modifiedDateTime,
          createdBy: written.createdBy,
          modifiedBy: written.modifiedBy,
          // Required on the record, so it is copied rather than conditionally
          // spread like the three below it.
          isDeleted: written.isDeleted,
          // The library flags ride along, or the row would show a disabled
          // lexicon as enabled for the moment between a save and the fetch
          // that follows it. Spread rather than defaulted: absent and false
          // are different, and only the absent ones tell you which records
          // predate the field.
          ...(written.displayOrder !== undefined
            ? { displayOrder: written.displayOrder }
            : {}),
          ...(written.enabled !== undefined
            ? { enabled: written.enabled }
            : {}),
          ...(written.autoApply !== undefined
            ? { autoApply: written.autoApply }
            : {}),
        };
        // Routed by the flag rather than written blind into `lexicons`: a
        // write can come back with `isDeleted` already true — a concurrent
        // soft delete by another admin — and `selectLexicons` does not filter
        // it out, because the settings page is where a disabled row gets
        // re-enabled and so shows everything in the list it is given. The soft
        // delete and restore paths use `LEXICON_SOFT_DELETE_CHANGED` below.
        return { ...rest, isMutating: false, ...intoItsSlot(state, summary) };
      })
      .addMatcher(LEXICON_MUTATION_BLOCKED_BY_AUDIT.match, (state, action) => ({
        ...state,
        isMutating: false,
        mutationNotice: { kind: 'blocked', message: action.payload.message },
      }))
      .addMatcher(LEXICON_MUTATION_AUDIT_INCOMPLETE.match, (state, action) => ({
        ...state,
        isMutating: false,
        mutationNotice: {
          kind: 'audit-incomplete',
          message: action.payload.message,
        },
      }))
      .addMatcher(LEXICON_MUTATION_FAILURE.match, (state, action) => ({
        ...state,
        isMutating: false,
        mutationNotice: { kind: 'failed', message: action.payload.message },
      }))
      // A conflict ends the in-flight state too — its surface is the drawer's
      // conflict banner (SET_LEXICON_CONFLICT above), not the notice.
      .addMatcher(SET_LEXICON_CONFLICT.match, (state) => ({
        ...state,
        isMutating: false,
      }))
      // So does a name-taken rejection (BR-2) — the saga bails before writing,
      // and its surface is the inline field error, not the notice.
      .addMatcher(SET_DRAFT_NAME_ERROR.match, (state) => ({
        ...state,
        isMutating: false,
      }))
      // Last word on the in-flight flag, dispatched from each mutation saga's
      // `finally`. Deliberately touches ONLY `isMutating`: it runs after an
      // outcome action, so clearing `mutationNotice` here would swallow the
      // very message that explains the failure.
      .addMatcher(LEXICON_MUTATION_SETTLED.match, (state) => ({
        ...state,
        isMutating: false,
      }))
      .addMatcher(CLEAR_MUTATION_NOTICE.match, (state) => {
        const { mutationNotice: _dropped, ...rest } = state;
        return rest;
      });
  }),
};

export type { LexiconsState };
